// =====================================================================
// Telegram WebApp bootstrap — module-level side effects only.
// No useState / useEffect / useReducer. State lives in the
// `useTelegramStore` zustand store, which is what consumers read.
// =====================================================================

import { useTelegramStore, type TelegramColorScheme } from "@/shared/store/telegram";
import type { JSX, ReactNode } from "react";
import type { TelegramWebApp } from "./types";

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

function detectWebApp(): TelegramWebApp | null {
  if (typeof window === "undefined") return null;
  return window.Telegram?.WebApp ?? null;
}

function injectScript(onLoad: () => void): void {
  if (typeof document === "undefined") return;
  const id = "telegram-web-app-script";
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.src = "https://telegram.org/js/telegram-web-app.js";
  s.async = true;
  s.onload = onLoad;
  document.head.appendChild(s);
}

function applyViewport(webApp: TelegramWebApp): void {
  const update = (): void => {
    const vh = webApp.viewportStableHeight || webApp.viewportHeight;
    if (typeof vh === "number" && vh > 0) {
      document.documentElement.style.setProperty("--tg-real-vh", `${vh}px`);
    }
  };
  update();
  webApp.onEvent("viewportChanged", update);
}

function applyTheme(webApp: TelegramWebApp): TelegramColorScheme {
  const scheme: TelegramColorScheme = webApp.colorScheme === "dark" ? "dark" : "light";
  useTelegramStore.getState().setColorScheme(scheme);
  document.documentElement.dataset.theme = scheme;
  return scheme;
}

function adopt(webApp: TelegramWebApp): void {
  const store = useTelegramStore.getState();
  webApp.ready();
  webApp.expand();
  // Убираем вертикальные свайпы Mini App — иначе при скролле появляется
  // «пустота» сверху/снизу (страницу можно оттянуть/закрыть жестом).
  webApp.disableVerticalSwipes?.();
  store.setWebApp(webApp);
  store.setReady(true);
  store.setInitData(webApp.initData ?? "");
  applyTheme(webApp);
  applyViewport(webApp);
  webApp.onEvent("themeChanged", () => {
    applyTheme(webApp);
  });
  webApp.onEvent("initDataChanged", () => {
    useTelegramStore.getState().setInitData(webApp.initData ?? "");
  });
}

const existing = detectWebApp();
if (existing) {
  adopt(existing);
} else if (typeof window !== "undefined") {
  injectScript(() => {
    const tg = detectWebApp();
    if (tg) adopt(tg);
    else useTelegramStore.getState().setReady(true);
  });
} else {
  useTelegramStore.getState().setReady(true);
}

export function TgProvider({ children }: { children: ReactNode }): JSX.Element {
  return <>{children}</>;
}
